import { IonContent } from '@ionic/react';
import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { NavigationRange } from '@/pages/Onboarding/components/NavigationRange';
import { MAX_USERNAME_LENGTH } from '@/pages/Onboarding/constants';
import { useOnboarding } from '@/pages/Onboarding/context';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { useNameAnumation } from '../../../screens/Name/hooks/useNameAnumation';
import { OwnAiTitle } from '../../components/Title';
import { LayoutDataType } from '../../types';

import { ScreenNameStyles } from './Name.styles';
import { RandomName } from './RandomName/RandomName';

export const ScreenName: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Name',
      });
    }
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const { nameResponseRef, play, responseRef, titleRef } = useNameAnumation();

  const [showInput, setShowinput] = useState<boolean>(true);
  const [userName, setUserName] = useState<string>('');

  const inputRef = useRef<HTMLIonInputElement | null>(null);

  const formName = form.watch('name');
  const disableNextStep = !formName;

  const submit = (): void => {
    if (formName) {
      setShowinput(false);
      setUserName(formName);
      play();
    }
  };

  const handleNextStep = (): void => {
    userService.createOrUpdate({
      name: formName,
    });

    if (shouldAnimate) {
      setTimeout(() => {
        setShowElements(false);
        nextStepTimeout(wizard.nextStep);
      }, 300);
    } else {
      nextStepTimeout(wizard.nextStep);
    }
  };

  const handleBackStep = () => {
    if (shouldAnimate) {
      setShowElements(false);
      nextStepTimeout(() => wizard.goToStep('security'));
    } else {
      wizard.goToStep('security');
    }
  };

  return (
    <ScreenNameStyles background="dark-violet">
      <NavigationRange onBack={handleBackStep} />
      <OwnAiTitle ref={titleRef} animate={shouldAnimate} show={showElements}>
        What would you like AI characters to call you?
      </OwnAiTitle>

      <IonContent>
        <div className="response">
          <OwnAiTitle ref={nameResponseRef}>{userName}</OwnAiTitle>
          <OwnAiTitle ref={responseRef} className="response-start">
            Nice to meet you, {formName}!
          </OwnAiTitle>
        </div>

        <FormInput
          form={form}
          hidden={!showInput}
          disableButton={disableNextStep}
          onSubmit={submit}
          ref={inputRef}
          name="name"
          placeholder="Type your name"
          type="text"
          maxLength={MAX_USERNAME_LENGTH}
        />
        {showInput && (
          <RandomName
            onComplete={(name) => {
              form.setValue('name', name);
            }}
          />
        )}
      </IonContent>

      <OnboardingFooter
        disabled={disableNextStep}
        onContinue={handleNextStep}
        hidden={showInput}
        buttonColor="pink"
      />
    </ScreenNameStyles>
  );
};
